import React, { useState, useEffect, useCallback } from 'react';
import client from '../../api/client';
import AdminTable from '../../components/Admin/AdminTable';
import ExcelUploadModal from '../../components/Admin/ExcelUploadModal';

const SAMPLE_COLS = ['oem', 'level', 'contact_name', 'designation', 'phone', 'email', 'notes'];

const EMPTY_FORM = {
  oem: '', level: 'L1', contact_name: '', designation: '',
  phone: '', email: '', notes: '', is_active: true,
};

const TABLE_COLS = [
  { key: 'oem', label: 'OEM' },
  { key: 'level', label: 'Level' },
  { key: 'contact_name', label: 'Contact Name' },
  { key: 'designation', label: 'Designation' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'is_active', label: 'Active', render: v => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{v ? 'Yes' : 'No'}</span>
  )},
];

export default function OEMEscalations() {
  const [allData, setAllData] = useState([]);
  const [filterOem, setFilterOem] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await client.get('/admin/oem-escalations/');
      setAllData(Array.isArray(res) ? res : res.results ?? []);
    } catch { setError('Failed to load OEM escalations.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const data = filterOem
    ? allData.filter(r => r.oem.toLowerCase().includes(filterOem.toLowerCase()))
    : allData;

  const openAdd = () => { setEditRow(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (row) => { setEditRow(row); setForm({ ...row }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editRow) {
        await client.put(`/admin/oem-escalations/${editRow.id}/`, form);
      } else {
        await client.post('/admin/oem-escalations/', form);
      }
      setShowForm(false);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || JSON.stringify(err?.response?.data) || 'Save failed.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await client.delete(`/admin/oem-escalations/${deleteRow.id}/`);
      setDeleteRow(null);
      load();
    } catch { alert('Delete failed.'); }
  };

  const handleUpload = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data: res } = await client.post('/admin/upload/oem-escalations/', fd);
    load();
    return res;
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'border border-gray-200 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-purple-300';

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">OEM Escalations</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage OEM escalation contacts by level (L1 / L2 / L3)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            Upload Excel
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Add Escalation
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-3 items-center">
        <input
          type="text"
          placeholder="Filter by OEM nameâ€¦"
          value={filterOem}
          onChange={e => setFilterOem(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 min-w-[240px]"
        />
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <AdminTable columns={TABLE_COLS} data={data} loading={loading} onEdit={openEdit} onDelete={setDeleteRow} />
      </div>

      {showUpload && (
        <ExcelUploadModal title="OEM Escalations" onUpload={handleUpload} onClose={() => setShowUpload(false)} sampleColumns={SAMPLE_COLS} templateEntity="oem-escalations" />
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">{editRow ? 'Edit Escalation' : 'Add OEM Escalation'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">OEM *</label><input className={inp} value={form.oem} onChange={e => set('oem', e.target.value)} required /></div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Level *</label>
                <select className={inp} value={form.level} onChange={e => set('level', e.target.value)} required>
                  <option value="L1">L1 â€“ First Response</option>
                  <option value="L2">L2 â€“ Technical Escalation</option>
                  <option value="L3">L3 â€“ Management Escalation</option>
                </select>
              </div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Contact Name *</label><input className={inp} value={form.contact_name} onChange={e => set('contact_name', e.target.value)} required /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Designation</label><input className={inp} value={form.designation} onChange={e => set('designation', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label><input className={inp} value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Email</label><input type="email" className={inp} value={form.email} onChange={e => set('email', e.target.value)} /></div>
              <div className="col-span-2"><label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label><textarea className={inp} rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="oem_active" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} className="w-4 h-4 accent-purple-600" />
                <label htmlFor="oem_active" className="text-sm text-gray-700">Active</label>
              </div>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">{saving ? 'Savingâ€¦' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Delete Escalation</h3>
            <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteRow.contact_name}</strong> ({deleteRow.oem} {deleteRow.level})? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteRow(null)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

